<template>
  <div class="card" data-qa="customer-card" @click="clickFullRow">
    <div class="header">
      <div class="title">
        <h4>{{ fullName }}</h4>
      </div>
      <span v-if="customer.KnownCustomer" class="info-label" data-qa="known-customer-label">
        <icon class="icon-info-label" icon="check" />Bekende klant
      </span>
    </div>

    <address itemscope itemtype="http://schema.org/Organization">
      <div itemprop="address">
        <template v-if="!showMore">
          <span v-if="customerAddressShort" itemprop="streetAddress">{{
            customerAddressShort
          }}</span>
        </template>
        <template v-if="showMore">
          <span v-if="customerAddressDetailed" itemprop="streetAddress">{{
            customerAddressDetailed
          }}</span>
          <br />
          <span v-if="customer.CustomerZipCode" itemprop="postal-code">{{
            customer.CustomerZipCode + ' '
          }}</span>
        </template>
        <template v-else-if="customerAddressShort && customer.CustomerCity">{{ ', ' }}</template>
        <span v-if="customer.CustomerCity" itemprop="locality">{{ customer.CustomerCity }}</span>
        <br />
      </div>
      <template v-if="customer.CustomerPhone && showMore">
        Telefoonnr:
        <span itemprop="telephone">{{ customer.CustomerPhone }}</span
        ><br />
      </template>
      <template v-if="customer.CustomerEmail && showMore">
        E-mailadres:
        <span itemprop="email" data-qa="customer-email">{{ customer.CustomerEmail }}</span
        ><br />
      </template>
      <template v-if="customer.PoId && showMore">
        POID: <span data-qa="customer-poid">{{ customer.PoId }}</span
        ><br />
      </template>
      <template v-if="customer.CustomerMagicNumber && showMore">
        Autoline klantnr:
        <span data-qa="customer-magic-number">{{ customer.CustomerMagicNumber }}</span
        ><br />
      </template>
      <template v-if="customer.CustomerRemark && showMore">
        <span class="sr-only">Remarks:</span>
        <span itemprop="remark" data-qa="customer-remark">{{ customer.CustomerRemark }}</span>
      </template>
      <template v-if="customerRelation">
        Relatie:
        <span itemprop="remark" data-qa="customer-relation">{{
          i18n.t(`/klantzoekenmodal/licenseplaterelation/${customerRelation}`)
        }}</span>
      </template>
    </address>

    <div class="buttons">
      <app-button
        button-class="inverted--bordered btn-toggle-details"
        data-qa="toggle-customer-details-button"
        @click.prevent.stop="toggleCustomerDetails"
      >
        {{
          i18n.t(
            showMore ? '/KlantZoekenModal/ToonMinderGegevens' : '/KlantZoekenModal/ToonMeerGegevens'
          )
        }}
      </app-button>
      <!-- todo show button after flow refinement -->
      <!-- <app-button
        v-if="wasSearchByLicencePlate"
        button-class="primary"
        data-qa="update-vehicle-customer-button"
        @click.stop="selectVehicleCustomerToChange"
      >
        Voertuig bijwerken
      </app-button> -->
      <app-button
        button-class="primary"
        data-qa="choose-customer-button"
        :disabled="selecting"
        @click.stop="selectCustomer"
      >
        Selecteer deze klant
      </app-button>
    </div>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { mapState } from 'vuex'

export default {
  components: {
    AppButton,
    Icon,
  },
  inject: ['isNationalCustomerSearch'],
  props: {
    customer: {
      type: Object,
      required: true,
    },
    wasSearchByLicencePlate: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrder: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectResult', 'selectVehicleCustomerToChange'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showMore: false,
      detailsRetrieved: false,
      selecting: false,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    customerRelation() {
      return this.customer.CustomerRelation?.toLowerCase()
    },
    customerAddressDetailed() {
      return this.customer.CustomerAddress || this.customer.CustomerStreet
    },
    customerAddressShort() {
      let detailedAddress = this.customerAddressDetailed
      if (!detailedAddress || !this.wasSearchByLicencePlate) return detailedAddress

      let commaSplitAddress = detailedAddress.split(',')
      if (commaSplitAddress.length > 2) return commaSplitAddress[2]

      return null
    },
    fullName() {
      return (
        (this.customer.CustomerTitle ? this.customer.CustomerTitle + ' ' : '') +
        ((this.dealerSettings.autolineKeyloopEnabled
          ? this.customer.CustomerFirstName
          : this.customer.CustomerInitials) || '') +
        (this.lastName ? ' ' + this.lastName : '')
      )
    },
    lastName() {
      return (
        (this.customer.CustomerMiddleName ? `${this.customer.CustomerMiddleName} ` : '') +
        (this.customer.CustomerFamilyName || '')
      )
    },
    searchEsb() {
      return !this.isNationalCustomerSearch() && this.dealerSettings.autolineKeyloopEnabled
    },
    isCompany() {
      return this.customer?.AutolineCustomerType == 'C'
    },
  },
  methods: {
    async selectCustomer() {
      if (this.selecting) return
      if (this.searchEsb && this.isPurchaseOrder) {
        this.selecting = true
        try {
          await this.getCustomerDetails()
        } catch {
          // Detail fetch failed; fall back to previous behavior
        } finally {
          this.selecting = false
        }
      }
      this.$emit('selectResult', this.customer)
    },
    selectVehicleCustomerToChange() {
      if (this.searchEsb) {
        this.$emit('selectVehicleCustomerToChange', this.customer)
      }
    },
    clickFullRow(_) {
      this.selectCustomer()
    },
    toggleCustomerDetails() {
      if (!this.showMore && this.searchEsb) {
        this.getCustomerDetails().then(() => {
          this.showMore = !this.showMore
        })
      } else {
        this.showMore = !this.showMore
      }
    },
    async getCustomerDetails() {
      if (!this.detailsRetrieved) {
        if (!this.isCompany) {
          await this.$store.dispatch(
            'customer/getCustomerByMagicNumber',
            this.customer.CustomerMagicNumber
          )
        } else {
          await this.$store.dispatch(
            'customer/searchCompanyByMagicNumberInCustomerSearch',
            this.customer.CustomerMagicNumber
          )
        }

        this.detailsRetrieved = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.card {
  border: 1px solid $gray-light;
  margin-bottom: $gutter;
  padding: $gutter;
  transition: background-color 0.2s ease-out;
  cursor: pointer;

  &:hover {
    background: #f8f8f8;
  }
}

.header {
  margin-bottom: 0.5em;

  @include breakpoint(md) {
    display: flex;
    align-items: flex-start;
  }
}

.title {
  margin-right: 0.5em;

  h4 {
    display: inline;
  }
}

.info-label {
  flex: 0 0 auto;
  background: $gray-lightest;
  margin-bottom: 1em;
  padding: 0.25em 0.5em;
  display: inline-flex;
  align-items: center;
  max-width: 100%;

  @include breakpoint(md) {
    display: flex;
    margin: 0 0 0 auto;
  }
}

.icon-info-label {
  @include icon();

  margin-right: 0.25em;
}

address {
  margin-bottom: 1em;
}

.buttons {
  display: flex;
  justify-content: space-between;
}

.btn-toggle-details {
  color: inherit;
}
</style>
